/**
 * Type definitions for Step 9: Data Fetch & Caching
 * Implements Feature Bible requirements with Wikipedia API alignment
 */

/**
 * Prefixed article identifier system
 * Following Wikipedia conventions with clear type discrimination
 */
export type ArticleId = 
  | `page:${number}`        // "page:736" - Wikipedia page ID (stable, numeric)
  | `title:${string}`       // "title:Albert Einstein" - Article title (human-readable)
  | `temp:${string}`;       // "temp:uuid-123" - Temporary/draft cards

/**
 * Parsed article identifier
 */
export interface ParsedArticleId {
  type: 'page' | 'title' | 'temp';
  value: string;
  originalId: ArticleId;
}

/**
 * Feature Bible compliant article interface
 * Extends ArticleCardData with content and metadata
 */
export interface SwipeArticle {
  // Feature Bible required fields
  id: ArticleId;                    // Prefixed identifier
  title: string;                    // Wikipedia article title
  url: string;                      // Wikipedia URL path
  canBack: boolean;                 // Set by context layer

  // Enhanced content fields
  content?: string;                 // HTML content for display
  extract?: string;                 // Plain text summary
  links?: ArticleLink[];            // Available navigation links
  metadata?: ArticleMetadata;       // Additional Wikipedia data

  // Wikipedia faithful rendering (from wikipediaFaithfulAPI)
  cssUrl?: string;                  // Wikipedia CSS URL for styling
  baseStyles?: string;              // Inline critical Wikipedia styles
}

/**
 * Navigation link information
 */
export interface ArticleLink {
  title: string;                    // Link target article title
  displayText?: string;             // Display text (may differ from title)
  exists: boolean;                  // Whether target article exists
  namespace?: number;               // Wikipedia namespace (0 = main articles)
  id?: ArticleId;                   // Resolved article ID if available
}

/**
 * Extended article metadata from Wikipedia API
 */
export interface ArticleMetadata {
  pageId?: number;                  // Numeric Wikipedia page ID
  lastModified?: string;            // ISO timestamp of last modification
  categories?: string[];            // Article categories
  thumbnail?: {                     // Article thumbnail image
    source: string;
    width: number;
    height: number;
  };
  coordinates?: {                   // Geographic coordinates if available
    lat: number;
    lon: number;
  };
  pageviews?: number;              // Recent page view count
  langlinks?: LanguageLink[];      // Links to other language versions
}

/**
 * Language link information
 */
export interface LanguageLink {
  lang: string;                     // Language code (e.g., 'es', 'fr')
  title: string;                    // Article title in that language
  url: string;                      // Full URL to language version
}

/**
 * Cache statistics and performance metrics
 */
export interface CacheStats {
  size: number;                     // Number of cached entries
  hits: number;                     // Cache hit count
  misses: number;                   // Cache miss count
  hitRate: number;                  // Hit rate percentage (0-1)
  memoryUsage: number;              // Approximate memory usage in bytes
}

/**
 * Service request options
 */
export interface ServiceOptions {
  useCache?: boolean;               // Whether to use cache (default: true)
  priority?: 'low' | 'normal' | 'high'; // Request priority
  includeContent?: boolean;         // Include HTML content (default: true)
  includeLinks?: boolean;           // Include article links (default: true)
  includeMetadata?: boolean;        // Include extended metadata (default: true)
  timeout?: number;                 // Request timeout in milliseconds
  source?: string;                  // Request source for logging/analytics
}

/**
 * Main service interface - Feature Bible compliant
 */
export interface SwipeUIFetchService {
  /**
   * Get article by ID - Feature Bible core requirement
   * @param id Prefixed article identifier (page:123, title:Article Name, etc.)
   * @param options Request configuration options
   * @returns Promise resolving to SwipeArticle
   */
  getArticle(id: ArticleId, options?: ServiceOptions): Promise<SwipeArticle>;
  
  /**
   * Get navigation links for an article
   * @param id Article identifier
   * @param limit Maximum number of links to return
   * @returns Promise resolving to array of navigation links
   */
  getArticleLinks(id: ArticleId, limit?: number): Promise<ArticleLink[]>;
  
  /**
   * Resolve title to page ID with caching
   * @param title Article title
   * @returns Promise resolving to Wikipedia page ID or null if not found
   */
  resolvePageId(title: string): Promise<number | null>;
  
  /**
   * Resolve page ID to current title
   * @param pageId Wikipedia page ID
   * @returns Promise resolving to current article title or null if not found
   */
  resolveTitle(pageId: number): Promise<string | null>;
  
  /**
   * Clear all caches
   */
  clearCache(): void;
  
  /**
   * Get cache performance statistics
   */
  getCacheStats(): CacheStats;
  
  /**
   * Batch resolve multiple identifiers to full article IDs
   * Useful for optimizing multiple lookups
   */
  batchResolveIds(identifiers: string[]): Promise<Map<string, ArticleId>>;
}

/**
 * Error types for Wikipedia service
 */
export class WikipediaServiceError extends Error {
  constructor(
    message: string,
    public code: 'INVALID_ID' | 'NOT_FOUND' | 'NETWORK_ERROR' | 'TIMEOUT' | 'PARSE_ERROR',
    public originalId?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'WikipediaServiceError';
  }
}

/**
 * ID resolution result
 */
export interface IdResolutionResult {
  resolved: ArticleId;
  fromCache: boolean;
  pageId?: number;
  title?: string;
}

/**
 * Cache entry structure for internal use
 */
export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  expires: number;
  accessCount: number;
  lastAccessed: number;
  size?: number;                    // Estimated size in bytes
}